import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { JSX, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { formaterDuree, tauxEnPourcents } from '@taki/data-model';
import { OptionList, SEUIL_RECHERCHE } from '../../components/OptionList.js';
import { Switch } from '../../components/Switch.js';
import { cn } from '../../lib/cn.js';
import {
  celluleDeListe,
  deviseDeColonne,
  editeurEnListe,
  enJauge,
  formatDeColonne,
  lignesDeCellule,
  lireBooleen,
  maxDeNote,
  moitiesDePeriode,
  motifDeRefus,
  saisieDePeriode,
  saisieNumerique,
  symboleDeDevise,
} from '../champs-types.js';
import type { Cell, EditorKind, Option, ReglagesDeColonne } from '../contract.js';
import { clefsDuChoixMultiple, elementsDeListe } from '../fields.js';
import { Etoiles, Jauge } from '../rendus-de-champ.js';
import { OTHER_LABEL } from '../project.js';

/**
 * L'EDITEUR D'UN CHAMP, EXTRAIT DE LA FICHE POUR ETRE PARTAGE.
 *
 * Ce fichier ne contient RIEN de nouveau du point de vue de la fiche : c'est
 * exactement le code qui vivait dans `RecordView.tsx` (les deux editeurs, leurs
 * classes, et les six conversions qui font passer une valeur de la donnee vers
 * l'espace de l'editeur et retour). Il a demenage pour une seule raison : la
 * fenetre de MODIFICATION GROUPEE (`BulkEditDialog`) saisit exactement les
 * memes types de champ, et un second jeu d'editeurs aurait diverge du
 * premier au premier ajustement, en silence.
 *
 * TROIS ETAGES, et c'est la seule chose a comprendre avant de lire :
 *
 * 1. LE VOCABULAIRE PARTAGE : la CLEF d'un champ (`fieldKeyOf`) et LES
 *    CONVERSIONS (`toDateInput`, `toTextInput`, `fromInput`,
 *    `normalizeForEditor`, `editorKindOf`). Elles n'ont ni etat ni rendu, et
 *    ce sont elles qui portent les pieges reels : deux espaces de cles qui ne
 *    se confondent pas, une date PURE qui ne traverse pas un fuseau, un nombre
 *    invalide qui ne s'ecrit pas, une valeur inchangee qui ne doit pas produire
 *    d'ecriture. Chacune est ecrite UNE fois : le premier defaut de ce chantier
 *    a ete de les recopier, et une regle recopiee diverge sans faire rougir un
 *    seul build.
 * 2. LE CONTROLE (`CellEditorControl`). Il REND un champ, un point. Il ne
 *    decide JAMAIS quand ecrire : il recoit une saisie, il en remonte une
 *    autre. C'est le seul endroit du produit ou l'on ecrit le balisage d'un
 *    `input`, d'un `textarea` ou d'un `select` de saisie.
 * 3. LA POLITIQUE, propre a chaque appelant :
 *    - la FICHE edite EN PLACE : `FieldEditorControl` ouvre, prend le focus, et
 *      ecrit des qu'on quitte le champ (pas de bouton Enregistrer) ;
 *    - la FENETRE DE LOT edite dans un FORMULAIRE : la valeur reste sous les
 *      doigts jusqu'au bouton d'application, et rien ne s'ecrit avant.
 *    Melanger les deux politiques dans un seul composant, c'est le premier pas
 *    vers deux composants.
 *
 * DEUX HABITS pour le meme controle (`variant`), et ce n'est pas cosmetique :
 * - `inline` : la boite d'un CHAMP DE FICHE. Meme hauteur, meme retrait et meme
 *   interligne que le texte en lecture, anneau interieur plutot que bordure :
 *   ouvrir un champ ne doit deplacer AUCUN pixel de ce qui est dessous.
 * - `form` : le champ de la FENETRE D'ACTION du produit (2.75rem de haut,
 *   rayon 0.75rem, fond `--surface-1`, libelle au-dessus en clair). C'est le
 *   vocabulaire que le prototype valide reprend pour la modification groupee.
 * - `cellule` : la case d'un TABLEAU en edition en place. Les mesures de
 *   `inline`, mais SANS cadre ni arrondi a lui : c'est la cellule entiere,
 *   carree, qui se surligne (`TableView.tsx`, chantier `ecran-fluide`). Un
 *   anneau arrondi DANS une case carree se lisait comme un champ pose dans un
 *   autre (captures du fondateur du 01/11).
 */

/* ------------------------------------------------------------------ */
/* Constantes partagees                                                */
/* ------------------------------------------------------------------ */

/**
 * Texte d'une valeur absente. JAMAIS de trou, jamais "undefined" a l'ecran.
 * Le mot est celui de la colonne fourre-tout (`OTHER_LABEL`), ecrit une fois.
 */
export const EMPTY_TEXT = OTHER_LABEL;

/**
 * Hauteur COMMUNE de la boite d'un champ de fiche, en lecture comme en edition.
 *
 * C'est la regle qui empeche la fiche de sauter : ouvrir un champ ne doit pas
 * pousser d'un pixel ce qui est en dessous. L'editeur faisait 32 px quand le
 * texte en lecture en faisait 24, donc tout le bas de la fiche descendait a
 * chaque clic. Les deux valent desormais la meme chose, et le conteneur porte
 * cette hauteur en minimum pour que la case ne bouge pas non plus quand le
 * champ est vide.
 */
export const FIELD_BOX = 'min-h-7';

/**
 * Habillage commun des editeurs EN PLACE : la boite d'un champ, pas celle d'un
 * formulaire. Meme hauteur, meme retrait lateral et meme interligne que le
 * texte en lecture, pour que le passage en edition ne decale RIEN, ni
 * verticalement ni horizontalement.
 *
 * Anneau interieur et non bordure : une vraie bordure decalerait le texte d'un
 * pixel vers l'interieur au moment precis ou l'utilisateur commence a taper.
 */
export const CONTROL_CLASSES = cn(
  '-mx-1.5 h-7 w-full min-w-0 rounded-sm px-1.5',
  'bg-surface ring-1 ring-inset ring-accent-border',
  'font-sans text-[0.9375rem] leading-[1.35] text-ink-900',
  'focus-visible:outline-none focus-visible:shadow-ring-soft',
);

/**
 * Le controle dans une CASE DE TABLEAU (habit `cellule`) : la hauteur de
 * `CONTROL_CLASSES` et la taille de texte du tableau, sans anneau ni arrondi.
 * Il prend toute la largeur de la zone posee sur la case ENTIERE
 * (`EditeurFlottant pleineCase`, fond plein : rien de l'ancienne valeur ne se
 * voit derriere), et ses retraits sont CEUX DE LA CASE, mesures
 * (`--retrait-gauche`, `--retrait-droit`) : le texte tape commence la ou
 * l'affichage commencait. Un nombre se cale a droite (`aDroite`), comme a
 * l'affichage (retour du fondateur du 02/11).
 */
export const CELL_CONTROL_CLASSES = cn(
  'h-7 w-full min-w-0 pl-[var(--retrait-gauche,1.1875rem)] pr-[var(--retrait-droit,1.1875rem)]',
  'bg-transparent',
  'font-sans text-[0.8125rem] leading-[1.35] text-ink-900 placeholder:text-ink-300',
  'focus-visible:outline-none',
);

/** Le retrait gauche d'une case de tableau, pour les editeurs qui ne sont pas un champ (etoiles, jauge, interrupteur). */
const RETRAIT_DE_CASE = 'pl-[var(--retrait-gauche,1.1875rem)]';

/**
 * L'editeur MULTILIGNE EN PLACE : memes tokens que le controle d'une ligne,
 * sans la hauteur figee. La hauteur vient de `rows`, calee sur le TEXTE en
 * cours d'edition (G6 : ouvrir un champ ne doit pas faire sauter ce qui est
 * dessous ; une hauteur fixe ecraserait un long texte ou gonflerait un champ
 * vide). `resize-y` laisse tirer la poignee au besoin.
 */
export const MULTILINE_CLASSES = cn(
  '-mx-1.5 w-full min-w-0 resize-y rounded-sm px-1.5 py-1',
  'bg-surface ring-1 ring-inset ring-accent-border',
  'font-sans text-[0.9375rem] leading-[1.35] text-ink-900',
  'focus-visible:outline-none focus-visible:shadow-ring-soft',
);

/**
 * Le champ d'une FENETRE D'ACTION (habit `form`), aux mesures du prototype
 * valide : 2.75rem de haut, rayon 0.75rem, fond `--surface-1`, bordure
 * `--border`, halo lime au focus. Il est plus BAS que le champ du hub
 * (`Input`, 3.125rem) : une fenetre empile trois champs et un pied dans 34rem
 * de large, elle ne peut pas payer la meme hauteur qu'un panneau de reglages.
 */
export const FORM_CONTROL_CLASSES = cn(
  'h-11 w-full min-w-0 rounded-[0.75rem] px-3.5',
  'border border-border bg-surface-1',
  'font-sans text-sm text-ink-900 placeholder:text-ink-300',
  'transition-colors hover:border-border-strong',
  'focus-visible:outline-none focus-visible:border-accent-border focus-visible:shadow-ring-soft',
);

/**
 * Le multiligne dans une CASE DE TABLEAU : il deborde de la case (plusieurs
 * lignes), donc il garde un cadre, mais CARRE comme la case qu'il prolonge.
 */
export const CELL_MULTILINE_CLASSES = cn(
  'w-full min-w-0 resize-y py-1 pl-[var(--retrait-gauche,1.1875rem)] pr-[var(--retrait-droit,1.1875rem)]',
  'bg-surface ring-1 ring-inset ring-accent-border',
  'font-sans text-[0.8125rem] leading-[1.35] text-ink-900',
  'focus-visible:outline-none',
);

/** Le multiligne de la meme fenetre : meme boite, hauteur libre, poignee verticale. */
export const FORM_MULTILINE_CLASSES = cn(
  'min-h-[5.5rem] w-full min-w-0 resize-y rounded-[0.75rem] px-3.5 py-3',
  'border border-border bg-surface-1',
  'font-sans text-sm leading-normal text-ink-900 placeholder:text-ink-300',
  'transition-colors hover:border-border-strong',
  'focus-visible:outline-none focus-visible:border-accent-border focus-visible:shadow-ring-soft',
);

/** Le nombre de lignes du textarea : le texte plus une, entre 4 et 24. */
export function multilineRows(draft: string): number {
  return Math.min(24, Math.max(4, draft.split('\n').length + 1));
}

/* ------------------------------------------------------------------ */
/* La CLEF D'UN CHAMP : la convention, ecrite UNE fois                 */
/* ------------------------------------------------------------------ */

/**
 * LA CLEF D'UN CHAMP, derivee de la CIBLE D'ECRITURE et de rien d'autre :
 * `role:<nom>` ou `col:<cle>`.
 *
 * ELLE VIT ICI PARCE QUE SES DEUX CONSOMMATEURS SONT ICI. La FICHE s'en sert
 * pour qu'une reponse d'ecriture retrouve SA case : `saveState.target` porte
 * exactement la meme cible (`SaveTarget`), donc deux ecritures en vol ne peuvent
 * plus se voler leur coche. La FENETRE DE LOT s'en sert pour designer le champ
 * choisi jusqu'a `writeCell`. Les deux espaces de cles peuvent collisionner (une
 * colonne `status` n'est PAS le role `status`), donc rien ne doit pouvoir les
 * confondre entre le moment ou l'on choisit le champ et celui ou l'on ecrit.
 *
 * Elle etait ecrite DEUX FOIS, a la lettre, une par consommateur, avec trois
 * commentaires pour affirmer que les deux formes etaient les memes. Rien dans le
 * code ne le garantissait : changer le prefixe d'un cote laissait le build vert
 * pendant que les deux espaces de cles divergeaient en silence. Une source,
 * donc, et la promesse est tenue par le code.
 *
 * Corollaire a ne pas "simplifier" : les roles libres (`row.extra`) prennent eux
 * aussi le prefixe `role:`, sinon leur case serait injoignable depuis une cible.
 * Aucune collision possible, un role libre n'est jamais un role connu du core.
 */
export function fieldKeyOf(kind: 'role' | 'column', key: string): string {
  return `${kind === 'role' ? 'role' : 'col'}:${key}`;
}

/* ------------------------------------------------------------------ */
/* Conversions : la donnee vers l'editeur, et retour                   */
/* ------------------------------------------------------------------ */

/** Valeur vide : rien a montrer, on affiche le texte d'absence en --ink-300. */
export function isBlankCell(value: Cell | undefined): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Le type d'editeur d'un champ : ce que l'ecran declare, sinon ce que la valeur
 * dit d'elle-meme. Deduire est un CONFORT, pas la regle : une colonne qui veut
 * un `select` doit le declarer, personne ne peut le deviner d'un texte.
 */
export function editorKindOf(value: Cell, declared: EditorKind | undefined): EditorKind {
  if (declared != null) return declared;
  if (typeof value === 'number') return 'number';
  if (value instanceof Date) return 'datetime';
  return 'text';
}

/**
 * La valeur pour un `<input type="date">`, en date PURE. On la fabrique sur les
 * composantes LOCALES et jamais avec `toISOString()`, qui recule d'un jour des
 * qu'on est a l'est de Greenwich (AGENTS.md section 7 : une date pure ne
 * traverse pas un fuseau).
 */
export function toDateInput(value: Cell): string {
  if (value instanceof Date) {
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }
  if (typeof value === 'string') return value.slice(0, 10);
  return '';
}

/** Un instant rendu dans le fuseau du navigateur pour `datetime-local`. */
export function toDatetimeInput(value: Cell): string {
  const instant = value instanceof Date ? value : typeof value === 'string' ? new Date(value) : null;
  if (instant === null || Number.isNaN(instant.getTime())) return '';
  const jour = toDateInput(instant);
  const heures = String(instant.getHours()).padStart(2, '0');
  const minutes = String(instant.getMinutes()).padStart(2, '0');
  return `${jour}T${heures}:${minutes}`;
}

/** Interprete les composantes locales sans passer par l'analyse UTC d'une date pure. */
export function fromDatetimeInput(raw: string): Date | null | undefined {
  if (raw === '') return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(raw);
  if (parts === null) return undefined;
  const [year, month, day, hour, minute] = parts.slice(1).map(Number);
  const instant = new Date(year!, month! - 1, day!, hour!, minute!);
  if (
    instant.getFullYear() !== year || instant.getMonth() + 1 !== month ||
    instant.getDate() !== day || instant.getHours() !== hour || instant.getMinutes() !== minute
  ) return undefined;
  return instant;
}

/** La valeur de depart d'un editeur texte ou nombre. */
export function toTextInput(value: Cell): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return toDateInput(value);
  return String(value);
}

/**
 * LE FORMAT DU NOMBRE QU'UN EDITEUR SAISIT (`formatDuNombre`, lu sur les
 * reglages de la colonne, son habillage compris), ou `null` : un editeur qui
 * n'est pas `number`, ou un nombre sans format.
 */
const formatSaisi = (kind: EditorKind, reglages: ReglagesDeColonne | undefined) =>
  kind === 'number' ? formatDeColonne('number', reglages) : null;

/**
 * LA SAISIE DE DEPART D'UN EDITEUR, dans son espace (toujours une chaine) :
 * la date et l'instant dans le format de leur champ natif, un Oui / Non en
 * `"true"` / `"false"` (une ancienne chaine comprise), un nombre a format
 * `duree` ecrit comme il se lit (« 2 h 30 », que `lireDuree` relit), un `taux`
 * en pourcents (« 21 % », que `lireTaux` relit), tout
 * autre nombre (montant, pourcentage) a la virgule, une liste un element par
 * ligne. Le reste, tel quel.
 */
export function brouillonDeSaisie(kind: EditorKind, value: Cell, reglages?: ReglagesDeColonne): string {
  if (kind === 'date') return toDateInput(value);
  if (kind === 'datetime') return toDatetimeInput(value);
  if (kind === 'boolean') {
    const lu = lireBooleen(value);
    return lu === null ? '' : String(lu);
  }
  if (kind === 'number') {
    if (typeof value !== 'number') return toTextInput(value);
    if (!Number.isFinite(value)) return String(value).replace('.', ',');
    const format = formatSaisi(kind, reglages);
    /* UN TAUX (fraction stockee) SE TAPE EN POURCENTS : 0,21 s'ouvre « 21 % ». */
    if (format === 'taux') return `${String(tauxEnPourcents(value)).replace('.', ',')} %`;
    return format === 'duree' ? formaterDuree(value) : String(value).replace('.', ',');
  }
  if (editeurEnListe(kind) && kind !== 'multiselect') return lignesDeCellule(value).join('\n');
  return toTextInput(value);
}

/**
 * La saisie relue en valeur brute. `undefined` = saisie INVALIDE : on ne
 * l'ecrit pas et on referme, plutot que d'ecrire un NaN dans la donnee.
 *
 * LES NOMBRES passent par la porte d'entree du data-model, SELON LE FORMAT que
 * portent les reglages : un nombre « 1,2 » (la virgule francaise, qu'un champ
 * `type=number` avalait : 12 en base), un montant « 1 250,50 € », une duree
 * « 2 h 30 » (150 minutes), une jauge hors de 0 a 100 refusee, une note
 * au-dela de son `max` refusee (`saisieNumerique`).
 * Une periode n'existe que ses DEUX jours poses ; une liste rend ses elements
 * un par ligne, sans doublon ni blanc, `null` quand il n'en reste aucun.
 */
export function fromInput(kind: EditorKind, raw: string, reglages?: ReglagesDeColonne): Cell | undefined {
  const text = raw.trim();
  if (kind === 'date') return text === '' ? null : text;
  if (kind === 'datetime') return fromDatetimeInput(text);
  if (kind === 'boolean') return text === '' ? null : lireBooleen(text) ?? undefined;
  if (kind === 'number' || kind === 'note') return saisieNumerique(kind, text, reglages);
  if (kind === 'periode') return saisieDePeriode(text);
  if (kind === 'etiquettes' || kind === 'liens' || kind === 'checklist') {
    return celluleDeListe(elementsDeListe(kind, raw));
  }
  return text;
}

/**
 * La valeur d'origine ramenee dans l'ESPACE de l'editeur. C'est la seule
 * comparaison honnete pour trancher "rien n'a change" : un editeur de date emet
 * une chaine `YYYY-MM-DD`, et confronter cette chaine a la `Date` d'origine
 * conclurait "different" a tous les coups. Ouvrir un champ puis en sortir
 * ecrirait alors, et cocherait, sans que rien n'ait bouge.
 */
export function normalizeForEditor(kind: EditorKind, value: Cell): Cell {
  if (kind === 'date') {
    const text = toDateInput(value);
    return text === '' ? null : text;
  }
  if (kind === 'datetime') {
    if (isBlankCell(value)) return null;
    const instant = value instanceof Date ? value : typeof value === 'string' ? new Date(value) : null;
    return instant !== null && !Number.isNaN(instant.getTime())
      ? fromDatetimeInput(toDatetimeInput(instant)) ?? null
      : null;
  }
  if (kind === 'number' || kind === 'note') {
    if (isBlankCell(value)) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  /* UN OUI / NON SE COMPARE EN BOOLEEN : l'interrupteur emet `true`, une fiche
     d'avant porte `"true"`, et c'est la meme case. */
  if (kind === 'boolean') return lireBooleen(value);
  if (kind === 'periode') return isBlankCell(value) ? null : String(value).trim();
  /* UNE LISTE SE COMPARE ELEMENT PAR ELEMENT, une ligne chacun, comme le choix
     multiple ci-dessous (mais sans couper a la virgule : une etiquette peut en
     porter une). Une liste de controle se compare sous sa forme stockee. */
  if (kind === 'etiquettes' || kind === 'liens') return celluleDeListe(lignesDeCellule(value));
  if (kind === 'checklist') return celluleDeListe(elementsDeListe('checklist', value));
  /* `lien` SE COMPARE COMME UN CATALOGUE : ce que l'editeur emet est une CHAINE
     (l'id de la fiche choisie) ou `null` (l'entree qui detache), donc la valeur
     d'origine doit etre ramenee dans le meme espace. Sans lui, une case de
     reference retomberait sur la branche texte, qui rend `''` la ou le
     selecteur rend `null` : refermer sans rien changer ecrirait quand meme. */
  if (kind === 'select' || kind === 'lien') return isBlankCell(value) ? null : String(value);
  /* UN CHOIX MULTIPLE SE COMPARE CLEF PAR CLEF, une par ligne : la lecture peut
     le rendre colle par des virgules (une app qui le lit elle-meme), l'editeur
     l'emet toujours une clef par ligne. Sans cette forme commune, la meme
     selection relue passerait pour « changee ». */
  if (kind === 'multiselect') {
    const clefs = clefsDuChoixMultiple(value);
    return clefs.length === 0 ? null : clefs.join('\n');
  }
  return toTextInput(value).trim();
}

/**
 * COCHER OU DECOCHER UNE CLEF d'un choix multiple : la liste sans elle si elle
 * y etait, avec elle sinon. Jamais de doublon, jamais de mutation de l'entree.
 */
export function basculerChoix(coches: readonly string[], clef: string): string[] {
  return coches.includes(clef) ? coches.filter((c) => c !== clef) : [...coches, clef];
}

/**
 * CE QU'UN CHOIX MULTIPLE ECRIT EN SE REFERMANT, dans l'espace de l'editeur.
 *
 * `undefined` = RIEN N'A CHANGE : la meme selection, dans n'importe quel ordre.
 * On referme alors sans ecrire, sinon ouvrir la liste et en sortir cocherait la
 * case « Enregistre » sans que rien n'ait bouge (la regle de tous les editeurs,
 * voir `normalizeForEditor`).
 *
 * SINON, LES CLEFS DANS L'ORDRE DU CATALOGUE (`ordre`), une par ligne, et non
 * dans l'ordre des clics : « Lundi, Vendredi » se relit pareil quelle que soit
 * la case cochee en premier. Une clef hors catalogue restee cochee suit, dans
 * son ordre d'origine. Tout decoche rend `null`, que la colonne ecrit `[]`.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : une selection qui s'ecrit dans le desordre
 * ou qui s'ecrit sans changement ne casse rien a l'ecran, elle se voit a la
 * relecture de la donnee, c'est-a-dire trop tard.
 */
export function choixMultipleEmis(
  depart: readonly string[],
  coches: readonly string[],
  ordre: readonly string[],
): Cell | undefined {
  const inchange =
    depart.length === coches.length && depart.every((clef) => coches.includes(clef));
  if (inchange) return undefined;
  const rangees = [
    ...ordre.filter((clef) => coches.includes(clef)),
    ...coches.filter((clef) => !ordre.includes(clef)),
  ];
  return rangees.length === 0 ? null : rangees.join('\n');
}

/* ------------------------------------------------------------------ */
/* Le CONTROLE : il rend un champ, il ne decide jamais quand ecrire     */
/* ------------------------------------------------------------------ */

/** Les deux habits du meme controle. Voir l'entete du fichier. */
export type CellEditorVariant = 'inline' | 'form' | 'cellule';

export interface CellEditorControlProps {
  /** Lequel des editeurs du contrat. */
  editor: EditorKind;
  /**
   * LA SAISIE EN COURS, toujours dans l'espace de l'editeur, donc toujours une
   * CHAINE (y compris pour un nombre et pour une date). C'est `fromInput` qui
   * la ramene en valeur de donnee, au moment ou l'appelant decide d'ecrire.
   */
  draft: string;
  /** La saisie a change. L'appelant en fait ce qu'il veut : la garder, ou ecrire. */
  onDraftChange(next: string): void;
  /** Catalogue de l'editeur `select`. Ignore par les quatre autres. */
  options: Option[];
  /** L'habit : la boite d'un champ de fiche, ou celle d'une fenetre d'action. */
  variant: CellEditorVariant;
  /**
   * Prendre le focus au montage (edition en place : un clic doit suffire, sans
   * ce focus il en faudrait un second pour taper). Un formulaire, lui, ne vole
   * jamais le focus : l'utilisateur y arrive par le champ d'avant.
   */
  autoFocus?: boolean;
  /** Sortie du champ. La politique d'ecriture appartient a l'appelant. */
  onBlur?(): void;
  /** Touches. Meme raison : `Entree` et `Echap` ne veulent pas dire la meme chose partout. */
  onKeyDown?(event: ReactKeyboardEvent<HTMLElement>): void;
  /** Nom accessible quand le controle n'a pas de `<label>` a lui (edition en place). */
  ariaLabel?: string;
  /**
   * LE TEXTE FANTOME DU CHAMP, quand la declaration en donne un
   * (`ViewColumn.placeholder`, `FieldMap.placeholder`, declares dans le Studio
   * champ par champ). Il se pose sur `input` et `textarea` : un `select` n'a
   * rien a suggerer (ses valeurs sont son catalogue), et un `input type=date`
   * rend deja `jj/mm/aaaa` de lui-meme.
   *
   * BROUILLON OU NON, ET C'EST VOULU : « ex. : Sony FX9 n°3 » aide autant a
   * corriger une fiche existante qu'a en creer une. Ce qui n'existe QUE sur un
   * brouillon, c'est le placeholder rendu en LECTURE, a la place de
   * « Non renseigne » (voir `RecordView`).
   */
  placeholder?: string;
  /**
   * LES REGLAGES D'AFFICHAGE DU CHAMP (`ReglagesDeColonne`) : le FORMAT du
   * nombre (une devise ou un habillage `euros`, un pourcentage, une duree,
   * une jauge) choisit la saisie de l'editeur `number`, l'unite et la devise
   * deviennent le SUFFIXE de la case (la meme chose qu'en lecture,
   * « 240 m² »), la note maximale le nombre d'etoiles. La valeur emise reste
   * le nombre nu.
   */
  reglages?: ReglagesDeColonne;
  /**
   * LE REFUS DE LA SAISIE EN COURS, une ligne (« Attendu : un nombre... ») :
   * le champ passe `aria-invalid` et le motif s'ecrit dessous, en
   * `role="alert"`, comme le font deja les reglages (`SettingsControls`,
   * `SelecteurCouleur`). Absent : rien a redire.
   */
  refus?: string;
  /**
   * LE TEXTE SE CALE A DROITE : un nombre, dans une case de tableau, reste a la
   * place ou il s'affichait (retour du fondateur du 02/11). Absent : a gauche.
   */
  aDroite?: boolean;
}

/**
 * LE SUFFIXE D'UNE CASE DE SAISIE (« m² », « € », « % ») : ce que la case dit
 * en lecture, pose au bord droit de l'editeur, pour qu'ouvrir un champ ne fasse
 * pas disparaitre l'unite de ce qu'on est en train de taper. Il suit le FORMAT
 * du nombre : le symbole de sa devise pour un montant, « % » pour un
 * pourcentage, son unite sinon. Une duree n'en a pas : « 2 h 30 » porte deja
 * ses unites.
 */
export function suffixeDe(editor: EditorKind, reglages: ReglagesDeColonne | undefined): string | undefined {
  if (editor !== 'number') return undefined;
  switch (formatSaisi(editor, reglages)) {
    case 'montant':
      return symboleDeDevise(deviseDeColonne('number', reglages));
    case 'pourcentage':
      return '%';
    case 'duree':
    case 'taux':
      return undefined;
    default:
      return reglages?.unite;
  }
}

/**
 * LA PLACE DU SUFFIXE DANS LA CASE, une seule regle pour toute unite et toute
 * devise, dans les deux habits (retour du fondateur, recette du 27/10 : « m² »
 * et « € » etaient colles au bord droit, voire dehors).
 *
 * - LE SUFFIXE EST DANS LA BOITE DU CHAMP : le calque qui le porte prend les
 *   marges de la boite (`-mx-1.5` en place), le champ le remplit, donc le
 *   bord droit du calque EST celui de la case. Le suffixe s'y pose a la MARGE
 *   INTERIEURE de la case (`px-1.5` en place, `px-3.5` dans une fenetre) :
 *   a la meme distance du bord droit que le texte du bord gauche.
 * - IL EST SUR LA LIGNE DE BASE DU TEXTE : meme corps et meme interligne que
 *   la saisie, centre sur toute la hauteur de la case comme la ligne du champ.
 * - LE TEXTE NE PASSE JAMAIS DESSOUS : la marge droite du champ vaut la marge
 *   interieure, plus un ecart, plus la largeur du suffixe, comptee GENEREUSE
 *   (`EM_PAR_SIGNE` par signe, la largeur d'un « m » ou d'un « % ») : un
 *   « CHF » ou un « km/h » reservent leur place comme un « € ».
 */
const EM_PAR_SIGNE = 0.75;
const PLACE_DU_SUFFIXE = {
  inline: { calque: '-mx-1.5', champ: 'mx-0', suffixe: 'right-1.5 text-[0.9375rem] leading-[1.35]', marge: '0.375rem' },
  cellule: { calque: '', champ: '', suffixe: 'right-[var(--retrait-droit,1.1875rem)] text-[0.8125rem] leading-[1.35]', marge: 'var(--retrait-droit, 1.1875rem)' },
  form: { calque: '', champ: '', suffixe: 'right-3.5 text-sm', marge: '0.875rem' },
} as const;

/** La marge droite d'un champ a suffixe : la marge interieure, un ecart, et la place du suffixe. */
export function margeDuSuffixe(suffixe: string, variant: CellEditorVariant): string {
  const signes = [...suffixe].length;
  return `calc(${PLACE_DU_SUFFIXE[variant].marge} + 0.375rem + ${signes * EM_PAR_SIGNE}em)`;
}

export function CellEditorControl({
  editor,
  draft,
  onDraftChange,
  options,
  variant,
  autoFocus = false,
  onBlur,
  onKeyDown,
  ariaLabel,
  placeholder,
  reglages,
  refus,
  aDroite = false,
}: CellEditorControlProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const idRefus = useId();
  const form = variant === 'form';
  /* Les classes d'un champ hors fenetre d'action : celles de la fiche, ou
     celles de la case de tableau (sans cadre a elles). */
  /* A droite, et dans la chasse fixe de l'affichage d'un nombre du tableau
     (`font-mono` de sa case) : les chiffres ne changent ni de place ni de forme. */
  const ligne = cn(variant === 'cellule' ? CELL_CONTROL_CLASSES : CONTROL_CLASSES, aDroite && 'text-right font-mono');
  /* Un editeur qui n'est pas un champ (interrupteur, jauge, etoiles) commence,
     dans une case de tableau, la ou son texte commencait. */
  const retrait = variant === 'cellule' ? cn(RETRAIT_DE_CASE, 'h-full') : undefined;
  const multiligne = variant === 'cellule' ? CELL_MULTILINE_CLASSES : MULTILINE_CLASSES;

  // Un clic doit suffire : sans ce focus, il en faudrait un second pour taper.
  useEffect(() => {
    if (!autoFocus) return;
    if (selectRef.current != null) {
      selectRef.current.focus();
      return;
    }
    if (textareaRef.current != null) {
      // Curseur en FIN de texte, jamais tout selectionne : sur un brief de
      // quinze lignes, la premiere frappe apres un select() effacerait tout.
      const zone = textareaRef.current;
      zone.focus();
      zone.setSelectionRange(zone.value.length, zone.value.length);
      return;
    }
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [autoFocus]);

  /* LE OUI / NON EST L'INTERRUPTEUR DU SOCLE (`Switch`), suivi de son mot : un
     clic bascule. Une case vide se lit eteinte et dit qu'elle n'a rien. */
  if (editor === 'boolean') {
    const lu = lireBooleen(draft);
    return (
      <span className={cn('flex items-center gap-2.5', form && 'h-11', retrait)} onKeyDown={onKeyDown}>
        <Switch checked={lu === true} onChange={(suivant) => onDraftChange(String(suivant))} aria-label={ariaLabel ?? 'Oui ou non'} />
        <span className={lu === null ? 'text-ink-300' : 'text-ink-700'}>{lu === null ? EMPTY_TEXT : lu ? 'Oui' : 'Non'}</span>
      </span>
    );
  }

  /* UN NOMBRE EN JAUGE EST SA JAUGE, meme dans une fenetre : un clic pose la
     valeur (de 0 a 100), les fleches l'avancent. */
  if (editor === 'number' && enJauge('number', reglages)) {
    const lue = draft.trim() === '' ? null : saisieNumerique('number', draft, reglages);
    return (
      <span className={cn('flex items-center', form && 'h-11', retrait)} onKeyDown={onKeyDown}>
        <Jauge
          valeur={typeof lue === 'number' ? lue : null}
          libelle={ariaLabel ?? 'Pourcentage'}
          onChange={(suivante) => onDraftChange(String(suivante))}
        />
      </span>
    );
  }

  /* LA NOTE EST SES ETOILES, meme dans une fenetre : un clic pose la note. */
  if (editor === 'note') {
    const lue = draft.trim() === '' ? null : Number(draft);
    return (
      <span className={cn('flex items-center', form && 'h-11', retrait)} onKeyDown={onKeyDown}>
        <Etoiles
          valeur={lue !== null && Number.isFinite(lue) ? lue : null}
          max={maxDeNote(reglages)}
          libelle={ariaLabel ?? 'Note'}
          onChange={(suivante) => onDraftChange(suivante === null ? '' : String(suivante))}
        />
      </span>
    );
  }

  /* UNE PERIODE, DEUX JOURS : la saisie est `debut/fin`, et quitter le premier
     jour pour le second n'est pas quitter le champ (la sortie ne compte qu'une
     fois le focus HORS des deux). */
  if (editor === 'periode') {
    const { debut, fin } = moitiesDePeriode(draft);
    const classes = form ? FORM_CONTROL_CLASSES : cn(ligne, 'mx-0 w-auto flex-1', variant === 'cellule' && 'px-1');
    return (
      <span
        role="group"
        aria-label={ariaLabel}
        className={cn(
          'flex w-full items-center gap-2',
          !form && variant !== 'cellule' && '-mx-1.5',
          variant === 'cellule' && 'pl-[var(--retrait-gauche,1.1875rem)] pr-[var(--retrait-droit,1.1875rem)]',
        )}
        onBlur={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
          onBlur?.();
        }}
        onKeyDown={onKeyDown}
      >
        <input
          ref={inputRef}
          type="date"
          aria-label="Début"
          className={classes}
          value={debut}
          onChange={(event) => onDraftChange(`${event.target.value}/${fin}`)}
        />
        <span aria-hidden className="text-ink-300">→</span>
        <input
          type="date"
          aria-label="Fin"
          className={classes}
          value={fin}
          min={debut === '' ? undefined : debut}
          onChange={(event) => onDraftChange(`${debut}/${event.target.value}`)}
        />
      </span>
    );
  }

  /* LES ETIQUETTES ET LA LISTE DE CONTROLE, DANS UN FORMULAIRE : un element
     par ligne. La fiche, elle, les edite EN PLACE (`core/ChampsRiches.tsx`). */
  if (editor === 'etiquettes' || editor === 'checklist') {
    return (
      <textarea
        ref={textareaRef}
        aria-label={ariaLabel}
        placeholder={placeholder ?? (editor === 'checklist' ? '[ ] Une tâche par ligne' : 'Une étiquette par ligne')}
        className={form ? FORM_MULTILINE_CLASSES : multiligne}
        rows={multilineRows(draft)}
        value={draft}
        onChange={(event) => onDraftChange(event.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
    );
  }

  /* DES LIENS SE COCHENT COMME UN CHOIX MULTIPLE, parmi les fiches de
     l'annuaire (le catalogue que l'appelant passe en `options`). */
  if (editor === 'multiselect' || editor === 'liens') {
    const selected = draft.split('\n').filter(Boolean);
    return (
      <div role="group" aria-label={ariaLabel} className="max-h-56 overflow-auto rounded-xl bg-surface-1 p-3" onKeyDown={onKeyDown}>
        {options.filter(option => !option.archived || selected.includes(option.value)).map(option => (
          <label key={option.value} className="flex cursor-pointer items-center gap-3 px-1 py-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              disabled={option.archived === true && !selected.includes(option.value)}
              onChange={() => onDraftChange(basculerChoix(selected, option.value).join('\n'))}
            />
            {option.label}
          </label>
        ))}
      </div>
    );
  }

  if (editor === 'select' || editor === 'lien') {
    const known = options.some((option) => option.value === draft);
    return (
      <select
        ref={selectRef}
        aria-label={ariaLabel}
        className={form ? FORM_CONTROL_CLASSES : ligne}
        value={draft}
        onChange={(event) => onDraftChange(event.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      >
        {/* Valeur absente ou hors catalogue : on la garde en tete de liste. La
            masquer ferait croire que le champ vaut deja la premiere option. */}
        {!known && <option value={draft}>{draft === '' ? EMPTY_TEXT : draft}</option>}
        {options.filter(option => !option.archived || option.value === draft).map((option) => (
          <option key={option.value} value={option.value} disabled={option.archived === true}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (editor === 'multiline') {
    return (
      <textarea
        ref={textareaRef}
        aria-label={ariaLabel}
        {...(placeholder === undefined ? {} : { placeholder })}
        className={form ? FORM_MULTILINE_CLASSES : multiligne}
        rows={multilineRows(draft)}
        value={draft}
        onChange={(event) => onDraftChange(event.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
    );
  }

  const suffixe = suffixeDe(editor, reglages);
  const place = PLACE_DU_SUFFIXE[variant];
  /* UNE DUREE SE TAPE EN MOTS (« 2 h 30 ») : ni clavier decimal, ni suffixe. */
  const enDuree = formatSaisi(editor, reglages) === 'duree';
  /* UN TAUX SE TAPE LUI AUSSI EN TEXTE (« 21 % », « 21 », « 0,21 ») : son
     « % » est dans la saisie, pas en suffixe, parce qu'un suffixe ferait lire
     « 0,5 » comme 0,5 %, quand la regle du taux le lit comme une fraction. */
  const enTaux = formatSaisi(editor, reglages) === 'taux';
  const champ = (
    <input
      ref={inputRef}
      type={editor === 'date' ? 'date' : editor === 'datetime' ? 'datetime-local' : 'text'}
      {...(editor === 'number' && !enDuree && !enTaux ? { inputMode: 'decimal' as const } : {})}
      aria-label={ariaLabel}
      {...(placeholder === undefined
        ? enDuree ? { placeholder: '2 h 30' } : enTaux ? { placeholder: '21 %' } : {}
        : { placeholder })}
      {...(suffixe === undefined ? {} : { style: { paddingRight: margeDuSuffixe(suffixe, variant) } })}
      className={cn(
        form ? FORM_CONTROL_CLASSES : ligne,
        suffixe !== undefined && place.champ,
        /* Le refus se voit aussi sur le cadre, comme `SelecteurCouleur`. */
        refus !== undefined && (form
          ? 'border-danger focus-visible:border-danger'
          /* Le halo de focus (accent) recouvrait le cadre : il s'eteint, le
             cadre passe a la couleur franche du refus. Dans une case de
             tableau, sans cadre a lui, le refus en prend un, carre. */
          : variant === 'cellule' ? 'ring-1 ring-inset ring-danger' : 'ring-danger focus-visible:shadow-none'),
      )}
      value={draft}
      {...(refus === undefined ? {} : { 'aria-invalid': true, 'aria-describedby': idRefus })}
      onChange={(event) => onDraftChange(event.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  );
  /* LE SUFFIXE, DANS LA CASE (`PLACE_DU_SUFFIXE`) : le calque prend la boite
     du champ, le suffixe s'y pose a la marge interieure, sur la ligne du
     texte. */
  const avecSuffixe = suffixe === undefined ? champ : (
    <span className={cn('relative block w-full', place.calque)}>
      {champ}
      <span
        aria-hidden
        data-suffixe=""
        className={cn('pointer-events-none absolute inset-y-0 flex items-center whitespace-nowrap font-sans text-ink-500', place.suffixe)}
      >
        {suffixe}
      </span>
    </span>
  );
  /* L'ARBRE NE CHANGE PAS DE FORME QUAND LE REFUS ARRIVE : le motif est un
     FRERE du champ, jamais une enveloppe posee autour. Envelopper l'aurait
     recree, donc lui aurait fait perdre le curseur : la frappe suivante
     tombait dans la page, et `Echap` fermait la fiche au lieu de la case. */
  return (
    <>
      {avecSuffixe}
      {refus !== undefined && (
        /* HORS DU FLUX, sous la case : le calque de l'editeur garde sa
           hauteur, la case ne remonte pas quand le motif arrive. */
        <span id={idRefus} role="alert" className="absolute left-0 top-full mt-1 whitespace-nowrap text-xs text-danger">
          {refus}
        </span>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* La POLITIQUE de la fiche : editer en place                          */
/* ------------------------------------------------------------------ */

/**
 * COMMENT UNE VALEUR A ETE VALIDEE. Present SEULEMENT quand c'est `Entree` (ou
 * Entree dans une liste) qui l'a validee : le tableau s'en sert pour passer a la
 * ligne suivante, la fiche l'ignore. Absent partout ailleurs (sortie du champ,
 * clic sur une option).
 */
export interface ValidationDeChamp {
  parEntree?: boolean;
}

/**
 * L'EDITEUR d'un champ DE FICHE. Il ne connait ni le SDK ni la ligne : il rend
 * un controle, et remonte une valeur brute.
 *
 * Trois sorties, et une seule ecrit : quitter le champ (clic ailleurs, Tab) et
 * `Entree` ecrivent ; `Echap` annule la saisie. `Echap` ne remonte JAMAIS au
 * document : hors editeur, la meme touche ferme la fiche, et les deux gestes
 * doivent rester distincts.
 *
 * EXCEPTION du MULTILIGNE : `Entree` y insere une ligne, c'est sa raison
 * d'etre ; l'ecriture part au blur ou par `Cmd/Ctrl+Entree`. `Echap` annule,
 * comme partout.
 *
 * IL N'ECRIT PLUS SON PROPRE BALISAGE : le champ vient de `CellEditorControl`,
 * partage avec la fenetre de modification groupee. Ce qui reste ici, et qui
 * n'appartient qu'a la fiche, c'est QUAND on ecrit.
 */
export function FieldEditorControl({
  editor,
  value,
  options,
  placeholder,
  reglages,
  entreeValide = false,
  variant = 'inline',
  aDroite = false,
  onCommit,
  onCancel,
}: {
  editor: EditorKind;
  /** L'habit : un champ de fiche (`inline`), ou la case d'un tableau (`cellule`). */
  variant?: 'inline' | 'cellule';
  /** Le texte cale a droite (un nombre dans une case de tableau). */
  aDroite?: boolean;
  value: Cell;
  options: Option[];
  /**
   * `Entree` VALIDE AUSSI UN TEXTE LONG (le tableau, qui descend d'une ligne a
   * chaque `Entree`) : `Maj+Entree` y insere alors le saut de ligne. Absent, c'est
   * la politique de la fiche, ou `Entree` saute la ligne et `Cmd/Ctrl+Entree`
   * ecrit.
   */
  entreeValide?: boolean;
  /**
   * LE TEXTE FANTOME DECLARE POUR CE CHAMP, relaye tel quel au controle. Il ne
   * change RIEN a la politique d'ecriture : un champ qu'on ouvre puis qu'on
   * quitte sans rien taper reste vide, le fantome n'est pas une valeur.
   */
  placeholder?: string;
  /** Les reglages d'affichage du champ (unite, devise), relayes au controle. */
  reglages?: ReglagesDeColonne;
  onCommit: (next: Cell, validation?: ValidationDeChamp) => void;
  onCancel: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState(() => brouillonDeSaisie(editor, value, reglages));
  const [refus, setRefus] = useState<string | null>(null);
  // Une case ne se referme qu'UNE fois : `Entree` puis le blur du demontage
  // ecriraient deux fois la meme valeur.
  const done = useRef(false);

  const finish = useCallback(
    (next: Cell | undefined, parEntree = false): void => {
      if (done.current) return;
      done.current = true;
      if (next === undefined) onCancel();
      else if (parEntree) onCommit(next, { parEntree: true });
      else onCommit(next);
    },
    [onCancel, onCommit],
  );

  if (editor === 'select') {
    /* CHOISIR EST UN GESTE COMPLET : le catalogue ecrit AU CHANGEMENT, sans
       attendre la sortie du champ. La saisie affichee n'a donc jamais a
       evoluer, l'editeur est demonte dans le meme geste. */
    const current = value == null ? '' : String(value);
    return (
      <CellEditorControl
        editor="select"
        variant={variant}
        aDroite={aDroite}
        autoFocus
        draft={current}
        options={options}
        onDraftChange={(next) => finish(next === '' ? null : next)}
        onBlur={() => finish(undefined)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            finish(undefined);
          }
        }}
      />
    );
  }

  /* UN INTERRUPTEUR, DES ETOILES OU UNE JAUGE ECRIVENT AU CLIC, comme un
     catalogue : le geste est complet. (La fiche les rend d'ordinaire en place,
     sans ouvrir d'editeur : ce chemin sert a qui monte cet editeur a la main.) */
  if (editor === 'boolean' || editor === 'note' || (editor === 'number' && enJauge('number', reglages))) {
    return (
      <CellEditorControl
        editor={editor}
        variant={variant}
        aDroite={aDroite}
        autoFocus
        draft={draft}
        options={options}
        {...(reglages === undefined ? {} : { reglages })}
        onDraftChange={(next) => finish(fromInput(editor, next, reglages))}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            finish(undefined);
          }
        }}
      />
    );
  }

  if (editor === 'multiline') {
    return (
      <CellEditorControl
        editor="multiline"
        variant={variant}
        aDroite={aDroite}
        autoFocus
        draft={draft}
        options={options}
        {...(placeholder === undefined ? {} : { placeholder })}
        onDraftChange={setDraft}
        onBlur={() => finish(fromInput(editor, draft))}
        onKeyDown={(event) => {
          // `Entree` nue insere une ligne : c'est tout l'interet du multiligne.
          // Le tableau la prend pour valider (`Maj+Entree` insere alors la ligne).
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey || (entreeValide && !event.shiftKey))) {
            event.preventDefault();
            finish(fromInput(editor, draft), true);
            return;
          }
          if (event.key === 'Escape') {
            event.stopPropagation();
            finish(undefined);
          }
        }}
      />
    );
  }

  return (
    <CellEditorControl
      editor={editor}
      variant={variant}
        aDroite={aDroite}
      autoFocus
      draft={draft}
      options={options}
      {...(placeholder === undefined ? {} : { placeholder })}
      {...(reglages === undefined ? {} : { reglages })}
      {...(refus === null ? {} : { refus })}
      onDraftChange={(next) => {
        setDraft(next);
        setRefus(null);
      }}
      onBlur={() => finish(fromInput(editor, draft, reglages))}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          /* UNE SAISIE ILLISIBLE NE REFERME PAS LA CASE EN SILENCE : `Entree`
             la garde ouverte, dit pourquoi sous le champ, et rien ne s'ecrit.
             La quitter (clic ailleurs, Tab, Echap) l'abandonne, comme avant. */
          const lu = fromInput(editor, draft, reglages);
          const motif = lu === undefined ? motifDeRefus(editor, draft, reglages) : null;
          if (motif !== null) {
            setRefus(motif);
            return;
          }
          finish(lu, true);
          return;
        }
        if (event.key === 'Escape') {
          event.stopPropagation();
          finish(undefined);
        }
      }}
    />
  );
}

/**
 * CE QUE VAUT UN CLIC SUR UNE OPTION D'UN CHOIX SIMPLE : la valeur a ecrire.
 *
 * Choisir l'option DEJA COCHEE (non vide) d'une liste `retirable` la retire :
 * `null`, que le SDK traduit en `deleteField()` sous le sac. Partout ailleurs le
 * choix est rendu tel quel, et un choix vide (`null`) reste vide : recliquer
 * sur « vide » ne retire rien de plus. Fonction pure et exportee expres : la
 * regle d'un geste qu'aucun build ne verifie.
 */
export function choixApresClic(
  courante: string,
  choisie: string | null,
  retirable: boolean,
): string | null {
  return retirable && choisie !== null && choisie !== '' && choisie === courante ? null : choisie;
}

/**
 * LA LISTE D'OPTIONS D'UN CATALOGUE, en place, dans la fiche.
 *
 * ELLE NE DESSINE PLUS RIEN : le panneau, le clavier, le clic dehors et le
 * retournement vivent dans `OptionList` (`components/OptionList.tsx`), promue
 * brique de `@taki/ui` par le lot 2 des liens entre portes, exactement comme
 * l'ancien en-tete de cette fonction l'annoncait. Ce qui reste ici, et qui
 * n'appartient qu'a un CATALOGUE, c'est la valeur HORS CATALOGUE : elle se
 * garde en tete de liste, sinon la case ferait croire qu'elle vaut deja la
 * premiere option.
 *
 * ELLE RESTE PROPRE A LA FICHE, et la fenetre de lot ne la reprend PAS : dans
 * une fenetre, un catalogue n'a pas de case a recouvrir ni de mise en page a
 * preserver, et le `select` natif y est le bon controle (c'est aussi ce que
 * montre le prototype valide).
 *
 * PAS D'ENTREE « Aucun » : la valeur vide d'un catalogue est une option comme
 * une autre (elle arrive par le repli hors catalogue), alors qu'un lien, lui,
 * se DETACHE. Deux gestes differents, deux listes qui ne se ressemblent qu'en
 * apparence.
 *
 * LE RETRAIT PASSE PAR LE RECLIC, PAS PAR UNE ENTREE DE PLUS : sur un champ
 * `retirable`, choisir l'option deja cochee la RETIRE (le champ redevient vide),
 * comme on decoche une etiquette, et le clavier suit (`Entree`, ou `Espace` sur
 * une liste sans recherche). Qui peut retirer est decide par l'appelant
 * (`proposeRetrait`, `RecordView.tsx`) : jamais un role (statut, titre), un
 * champ obligatoire ni un champ natif. La regle du geste est `choixApresClic`.
 */
export function FieldOptionList({
  anchorLabel,
  value,
  options,
  retirable = false,
  onCommit,
  onCancel,
}: {
  /**
   * Le texte du champ, redessine par-dessus la case pour qu'elle garde sa place
   * pendant que la liste est ouverte. Absent quand l'appelant montre DEJA son
   * propre element (la pastille de statut de l'en-tete) : on n'en dessine pas
   * un second par-dessus.
   */
  anchorLabel?: ReactNode;
  value: Cell;
  options: Option[];
  /** Recliquer l'option cochee la retire (voir `choixApresClic`). Faux par defaut : un role ne se retire pas. */
  retirable?: boolean;
  onCommit: (next: Cell, validation?: ValidationDeChamp) => void;
  onCancel: () => void;
}): JSX.Element {
  const current = value == null ? '' : String(value);
  const known = options.some((option) => option.value === current);
  // Valeur absente ou hors catalogue : on la garde EN TETE. La masquer ferait
  // croire que le champ vaut deja la premiere option.
  const entries: Option[] = known
    ? options.filter(option => !option.archived || option.value === current)
    : [
        // `neutral` : une valeur hors catalogue n'a pas de ton declare, et lui en
        // inventer un lui donnerait un sens qu'elle n'a pas.
        { value: current, label: current === '' ? EMPTY_TEXT : current, tone: 'neutral' },
        ...options.filter(option => !option.archived),
      ];
  return (
    <OptionList
      {...(anchorLabel === undefined ? {} : { anchorLabel })}
      value={current}
      options={entries.map((option) => ({
        ...option,
        label: option.label === '' ? EMPTY_TEXT : option.label,
      }))}
      /* UN LONG CATALOGUE SE CHERCHE. Le seuil vit dans la brique, une fois :
         le selecteur de fiche liee et le catalogue d'un statut doivent basculer
         au meme endroit, sinon l'un des deux se cherche et l'autre non sans
         qu'on sache pourquoi. */
      recherche={entries.length > SEUIL_RECHERCHE}
      onPick={(next, parEntree) => {
        if (entries.find((o) => o.value === next)?.archived) return;
        const choix = choixApresClic(current, next, retirable);
        /* `Entree` dans la liste valide, un clic choisit seulement : le tableau
           ne descend d'une ligne que pour le premier. */
        if (parEntree === true) onCommit(choix, { parEntree: true });
        else onCommit(choix);
      }}
      onClose={onCancel}
    />
  );
}

/**
 * LE CHOIX MULTIPLE, en place, dans la fiche : la MEME liste que le catalogue
 * (`FieldOptionList`), ou chaque clic coche ou decoche au lieu de choisir.
 *
 * LA LISTE RESTE OUVERTE PENDANT QU'ON COCHE : refermer au premier clic
 * obligerait a rouvrir le champ pour chaque jour d'un plan. Les coches vivent
 * donc ICI, en memoire, et l'ECRITURE PART A LA FERMETURE, une seule fois,
 * quel que soit le geste qui ferme : clic dehors, Tab, clic sur la case, et
 * Echap aussi. Echap n'annule PAS, et c'est la difference avec un champ texte :
 * ici chaque coche s'est deja VUE, et effacer d'une touche ce que l'on a sous
 * les yeux depuis dix clics serait une perte, pas une annulation.
 *
 * RIEN N'A CHANGE, RIEN NE S'ECRIT (`choixMultipleEmis`) : ouvrir la liste pour
 * relire les jours puis la refermer ne produit ni ecriture ni coche verte.
 *
 * UNE CLEF HORS CATALOGUE se garde EN TETE, cochee, comme la valeur hors
 * catalogue d'un `select` : la masquer la ferait disparaitre de la donnee a la
 * premiere fermeture, sans que personne l'ait decochee.
 */
export function FieldMultiOptionList({
  anchorLabel,
  value,
  options,
  onCommit,
  onCancel,
}: {
  /** Le texte du champ, redessine par-dessus la case (voir `FieldOptionList`). */
  anchorLabel?: ReactNode;
  value: Cell;
  options: Option[];
  onCommit: (next: Cell) => void;
  onCancel: () => void;
}): JSX.Element {
  const [depart] = useState(() => clefsDuChoixMultiple(value));
  const [coches, setCoches] = useState<readonly string[]>(depart);
  /* LES COCHES EN REF AUSSI : la fermeture arrive par un ecouteur du document
     (le clic dehors), qui doit lire la DERNIERE selection et non celle du
     rendu ou il a ete pose. */
  const cochesRef = useRef<readonly string[]>(depart);
  // Une liste ne se referme qu'UNE fois : Echap puis le clic dehors du meme
  // geste ecriraient deux fois.
  const done = useRef(false);

  /* L'entree a valeur vide n'est pas une clef : cocher « rien » n'a pas de
     sens dans un choix multiple, c'est tout decocher qui le dit. Une clef
     hors catalogue passe en tete ; `neutral`, elle n'a pas de ton declare. */
  const entries = useMemo((): Option[] => {
    const catalogue = options.filter((option) => option.value !== '' && (!option.archived || depart.includes(option.value)));
    const hors: Option[] = depart
      .filter((clef) => !catalogue.some((option) => option.value === clef))
      .map((clef) => ({ value: clef, label: clef, tone: 'neutral' }));
    return [...hors, ...catalogue];
  }, [depart, options]);

  const fermer = useCallback((): void => {
    if (done.current) return;
    done.current = true;
    const next = choixMultipleEmis(
      depart,
      cochesRef.current,
      entries.map((option) => option.value),
    );
    if (next === undefined) onCancel();
    else onCommit(next);
  }, [depart, entries, onCancel, onCommit]);

  return (
    <OptionList
      {...(anchorLabel === undefined ? {} : { anchorLabel })}
      value={null}
      coches={coches}
      options={entries.map((option) => ({
        ...option,
        label: option.label === '' ? option.value : option.label,
      }))}
      recherche={entries.length > SEUIL_RECHERCHE}
      onPick={(clef) => {
        if (clef == null || entries.find(o => o.value === clef)?.archived && !cochesRef.current.includes(clef)) return;
        const suivantes = basculerChoix(cochesRef.current, clef);
        cochesRef.current = suivantes;
        setCoches(suivantes);
      }}
      onClose={fermer}
    />
  );
}
